import { Divider, Stack, Typography, styled } from "@mui/material"
import { isValid } from "date-fns"
import { formatDateWithDots } from "../../../services"
import { type SourceName, sourceEnum } from "../../../types"
import { isDefined, labelOf, pluralize } from "../../../utils"

interface SalesCardTooltipProps {
  period?: string | null
  sources?: SourceName[]
  tooltipMsg?: string
}

export const SalesCardTooltip = ({
  period,
  sources = [],
  tooltipMsg,
}: SalesCardTooltipProps) => {
  const hasSources = sources.length > 0
  const hasPeriod = isDefined(period)
  const hasMsg = isDefined(tooltipMsg)

  if (!hasSources && !hasPeriod && !hasMsg) {
    return null
  }

  const sourcesLabel = sources
    .map((source) => labelOf(sourceEnum, source))
    .join(", ")

  return (
    <Stack gap={1} sx={{ maxWidth: "200px" }}>
      {hasSources && (
        <TooltipLabel>
          {pluralize(sources.length, "Source", "Sources")} :{" "}
          <TooltipData>{sourcesLabel}</TooltipData>
        </TooltipLabel>
      )}
      {hasPeriod && (
        <TooltipLabel>
          Données du{" "}
          <TooltipData>
            {isValid(new Date(period)) ? formatDateWithDots(period) : period}
          </TooltipData>
        </TooltipLabel>
      )}
      {hasMsg && (
        <>
          {(hasSources || hasPeriod) && <Divider />}
          <TooltipMsg>{tooltipMsg}</TooltipMsg>
        </>
      )}
    </Stack>
  )
}

const TooltipLabel = styled(Typography)({})
TooltipLabel.defaultProps = {
  variant: "12px",
}

const TooltipData = styled(TooltipLabel)({
  fontWeight: 600,
})

const TooltipMsg = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontStyle: "italic",
  color: theme.palette.primary.dark3,
  textWrapStyle: "pretty",
}))

/**
 * This feature prevents the tooltip container from being displayed if SalesCardTooltip returns null instead of a JSX element. Be careful, however, as using hooks such as useEffect in SalesCardTooltip can cause issues because it is not a component that is initialized in the traditional way.
 */
export const renderTooltip = (props: SalesCardTooltipProps) =>
  SalesCardTooltip(props) ?? undefined
